import { DeleteOutlined, UploadOutlined } from '@ant-design/icons'
import { Alert, Button, Card, Space, Typography, Upload } from 'antd'
import React from 'react'
import styled from 'styled-components'

const UploadSummary = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`

export interface CalibrationFileUploadMetadata {
  originalName?: string
  parseWarnings?: string[]
  uploadWarning?: string
}

interface CalibrationFileUploadProps {
  disabled?: boolean
  isParsing: boolean
  isUploading: boolean
  parseError?: string
  metadata?: CalibrationFileUploadMetadata
  onUpload: (file: File) => Promise<void>
  onDelete: () => Promise<void>
}

const UploadedFileCard = styled(Card)`
  .ant-card-body {
    padding: 0.875rem 1rem;
  }
`

const UploadedFileRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
`

const UploadedFileName = styled(Typography.Text)`
  min-width: 0;
  flex: 1;
`

export const CalibrationFileUpload: React.FC<CalibrationFileUploadProps> = ({
  disabled,
  isParsing,
  isUploading,
  parseError,
  metadata,
  onUpload,
  onDelete,
}) => {
  const isWorking = isParsing || isUploading
  const uploadButton = (
    <Upload
      accept='.nde'
      beforeUpload={(file) => {
        void onUpload(file)
        return false
      }}
      disabled={disabled || isWorking}
      multiple={false}
      showUploadList={false}>
      <Button icon={<UploadOutlined />} loading={isWorking}>
        {metadata?.originalName ? 'Replace' : 'Upload Calibration NDE File'}
      </Button>
    </Upload>
  )
  const deleteButton = (
    <Button
      danger
      icon={<DeleteOutlined />}
      disabled={disabled || isWorking}
      onClick={() => {
        void onDelete()
      }}>
      Delete
    </Button>
  )

  return (
    <UploadSummary>
      <Typography.Text type='secondary'>
        Uploading a new file will overwrite autofillable calibration fields.
      </Typography.Text>

      {metadata?.originalName ? (
        <UploadedFileCard size='small'>
          <UploadedFileRow>
            <UploadedFileName ellipsis={{ tooltip: metadata.originalName }}>
              {metadata.originalName}
            </UploadedFileName>
            <Space size='small'>
              {uploadButton}
              {deleteButton}
            </Space>
          </UploadedFileRow>
        </UploadedFileCard>
      ) : (
        uploadButton
      )}

      {parseError && (
        <Alert
          type='error'
          showIcon
          title='Calibration NDE parse failed'
          description={parseError}
        />
      )}

      {metadata?.uploadWarning && (
        <Alert
          type='warning'
          showIcon
          title='Autofill applied, historical upload failed'
          description={metadata.uploadWarning}
        />
      )}

      {!!metadata?.parseWarnings?.length && (
        <Alert
          type='warning'
          showIcon
          title='Calibration NDE parsed with warnings'
          description={metadata.parseWarnings.join(' ')}
        />
      )}
    </UploadSummary>
  )
}
